import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useCountUp from '../hooks/useCountUp';
import '../styles/responsive/pages/Balance.css';
import '../styles/enterprise.css';
import {
  AlertTriangle,
  RefreshCw,
  CheckCircle,
  Info,
  Send,
  Repeat,
  Clock,
  Lock,
  Receipt,
  ArrowRight,
} from 'lucide-react';
import { RecentTransactionsTable } from '../components/common/RecentTables';
import { useTheme } from '../context/ThemeContext';
import { fetchWithAuth } from '../utils/api';
import { formatUSD, formatCount } from '../utils/formatAmount';
import { deriveCounts } from '../utils/stats';
import PageHeaderPro from '../components/common/PageHeaderPro';
import StatCard from '../components/common/StatCard';
import { TokenLogo } from '../components/common/CryptoLogo';
import { chainDisplayLabel } from '../utils/assetLogos';
import Skeleton from '../components/common/Skeleton';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const OverpaidPayment = ({ payment }) => {
  const isSession = payment.type === 'session';
  // A session is identified to the merchant by the order it belongs to — the
  // session id is ours, the order id is theirs and is what they can look up in
  // their own system. Falls back to the session id when no order was supplied.
  const id = isSession ? payment.orderId || payment.sessionId : payment.invoiceId;
  const amount = payment.amount || 0;
  const amountPaid = payment.amountPaid || payment.totalPaidUsd || 0;
  const excess = amountPaid - amount;

  // The asset the payment arrived in. The excess settles in this token on this
  // chain, so it is part of the record rather than a detail.
  const token = payment.token || payment.tokenSymbol || null;
  const chain = payment.network || payment.networkName || payment.chain || null;

  const paidAt = payment.paidAt || payment.createdAt || null;
  const when = paidAt ? new Date(paidAt) : null;

  return (
    <tr>
      <td>
        <span className="op-date">
          {when ? when.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}
        </span>
        <span className="op-time">
          {when ? when.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : ''}
        </span>
      </td>
      <td>
        <span className="op-customer">
          {payment.customerName || payment.customerEmail || 'Customer'}
        </span>
      </td>
      <td>
        <span className={`bal-type ${isSession ? 'session' : 'invoice'}`}>
          {isSession ? 'Order' : 'Invoice'}
        </span>
        <span className="op-id mono" title={id || ''}>
          {!id ? '—' : String(id).length > 18 ? `${String(id).slice(0, 18)}…` : String(id)}
        </span>
      </td>
      <td>
        {token ? (
          <div className="op-asset">
            <TokenLogo symbol={token} size={22} />
            <div>
              <span className="op-token">{token}</span>
              {chain && <span className="op-chain">{chainDisplayLabel(chain) || chain}</span>}
            </div>
          </div>
        ) : (
          <span className="op-muted">—</span>
        )}
      </td>
      <td className="op-num"><span className="op-plain">${formatUSD(amount)}</span></td>
      <td className="op-num"><span className="op-plain">${formatUSD(amountPaid)}</span></td>
      {/* The figure the row exists for. */}
      <td className="op-num"><span className="op-excess">+${formatUSD(excess)}</span></td>
    </tr>
  );
};

const Balance = () => {
  const { mode } = useTheme();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [overpaidPayments, setOverpaidPayments] = useState([]);
  const [payoutBalance, setPayoutBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // PRD §3.3: conversions still in a non-terminal state.
  const [pendingConversions, setPendingConversions] = useState([]);

  // Only the newest request may write to the page. Requests for Test and Live
  // can be in flight together (a reload, or a quick toggle); without this,
  // whichever answered LAST won, so test figures could replace live ones.
  const requestSeq = useRef(0);

  const fetchData = useCallback(async (isRefresh = false) => {
    const seq = ++requestSeq.current;
    const isStale = () => seq !== requestSeq.current;
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(false);

      const isTestnet = mode === 'test' ? 'true' : 'false';

      const [statsRes, transactionsRes, overpaidInvoicesRes, overpaidSessionsRes, payoutBalanceRes] = await Promise.all([
        fetchWithAuth(`${API_BASE_URL}/invoices/balance-stats?isTestnet=${isTestnet}`),
        // Same noise rules as the Transactions page: no dust, and no untracked
        // payments in a token the merchant does not accept.
        fetchWithAuth(`${API_BASE_URL}/transactions?limit=5&sortBy=created_at&sortOrder=DESC&isTestnet=${isTestnet}&hideDust=true&hideUntrackedUnsupported=true`),
        fetchWithAuth(`${API_BASE_URL}/invoices?status=overpaid&limit=5&sortBy=updated_at&sortOrder=DESC&isTestnet=${isTestnet}`),
        fetchWithAuth(`${API_BASE_URL}/v1/payments/my-sessions?status=overpaid&limit=5&isTestnet=${isTestnet}`),
        fetchWithAuth(`${API_BASE_URL}/payouts/my-balance?isTestnet=${isTestnet}`),
      ]);

      const [statsData, transactionsData, overpaidInvoicesData, overpaidSessionsData, payoutBalanceData] = await Promise.all([
        statsRes.json(),
        transactionsRes.json(),
        overpaidInvoicesRes.json(),
        overpaidSessionsRes.json(),
        payoutBalanceRes.json(),
      ]);

      // A newer request (e.g. for the other mode) has started: drop this one.
      if (isStale()) return;

      if (statsData.success) setStats(statsData.data);
      if (transactionsData.success) setRecentTransactions(transactionsData.data?.transactions || []);
      if (payoutBalanceData.success) setPayoutBalance(payoutBalanceData.data);

      const invoices = (overpaidInvoicesData.success ? overpaidInvoicesData.data?.invoices || [] : [])
        .map(inv => ({ ...inv, type: 'invoice' }));
      const sessions = (overpaidSessionsData.success ? overpaidSessionsData.data?.sessions || [] : [])
        .map(sess => ({ ...sess, type: 'session', totalPaidUsd: sess.totalPaidUsd || sess.amount }));

      const combined = [...invoices, ...sessions]
        .sort((a, b) => new Date(b.paidAt || b.createdAt) - new Date(a.paidAt || a.createdAt))
        .slice(0, 5);
      setOverpaidPayments(combined);
      setLastUpdated(new Date());
    } catch (error) {
      if (isStale()) return;
      console.error('Failed to fetch balance data:', error);
      setError(true);
    } finally {
      // The newest request owns the spinner.
      if (!isStale()) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [mode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // PRD §3.3: summarize conversions still in a non-terminal state.
  const fetchPendingConversions = useCallback(async () => {
    const statuses = ['queued', 'quoting', 'quoted', 'executing'];
    try {
      const results = await Promise.all(
        statuses.map(async (status) => {
          const response = await fetchWithAuth(
            `${API_BASE_URL}/merchant/conversions?status=${status}&limit=200`,
          );
          const data = await response.json();
          return data?.success ? data.data?.conversions || [] : [];
        }),
      );
      setPendingConversions(results.flat());
    } catch (err) {
      console.error('Failed to fetch pending conversions:', err);
    }
  }, []);

  useEffect(() => {
    fetchPendingConversions();
  }, [fetchPendingConversions]);


  const totalRevenue = (stats?.invoices?.totalPaid || 0) + (stats?.sessions?.totalPaid || 0);
  const totalOverpaid = (stats?.invoices?.totalOverpaid || 0) + (stats?.sessions?.totalOverpaid || 0);

  // The single withdrawable figure the page works from: settled less in-flight
  // payouts and in-flight refunds.
  const availableBalance = payoutBalance?.availableBalance || 0;
  const pendingPayoutAmount = payoutBalance?.pendingPayoutAmount || 0;
  // Same figure as "Total Revenue" on the Overview (invoice + checkout session
  // payments), so the two cards never disagree. Payments with no invoice or
  // session behind them (direct deposits) are not counted here, although they
  // are still part of the withdrawable balance above.
  const lifetimeReceived = stats ? totalRevenue : null;
  // Pending settlement (invoices/sessions not yet settled) from stats.
  const pendingSettlement = (stats?.invoices?.totalRemaining || 0) + (stats?.sessions?.totalRemaining || 0);

  const counts = deriveCounts(stats);
  const totalPaidCount = counts.combined.settledCount;
  const pendingCount = (counts.invoice.pendingCount || 0) + (counts.session.pendingCount || 0);

  // PRD §3.3: count + USD total of conversions still settling.
  const pendingConversionCount = pendingConversions.length;
  const pendingConversionUsd = pendingConversions.reduce(
    (sum, c) => sum + (Number(c.source_amount_usd) || 0),
    0,
  );

  const updatedText = lastUpdated
    ? `Updated ${lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
    : 'Loading…';

  const headerActions = (
    <>
      <button className="ent-btn ent-btn-secondary" onClick={() => fetchData(true)} disabled={refreshing}>
        <RefreshCw size={16} className={refreshing ? 'spinning' : ''} />
        {refreshing ? 'Refreshing' : 'Reconcile now'}
      </button>
      <button className="ent-btn ent-btn-primary" onClick={() => navigate('/dashboard/payouts')}>
        Request payout
      </button>
    </>
  );

  // Counts the headline figure into place. Fed the real availableBalance, and
  // the hook writes the exact target on its final frame, so what settles on
  // screen is the true value rather than an eased approximation of it.
  const shownBalance = useCountUp(availableBalance);

  const dash = '—';

  return (
    <div className="ent ent-page balance-page">
      <PageHeaderPro
        title="Balance"
        mode={mode === 'test' ? 'test' : 'live'}
        updatedText={updatedText}
        actions={headerActions}
      />

      {/* Reconciliation banner. No reconciliation endpoint exists, so this is an
          honest neutral/informational banner — never a fake "all clear". */}
      <div className="bal-recon bal-recon--info">
        <Info size={18} />
        <div className="bal-recon__body">
          <strong>Reconciliation not yet available.</strong>
          <span> Ledger-vs-on-chain reconciliation reports are not enabled for this account.</span>
        </div>
        <span className="bal-recon__meta">{lastUpdated ? updatedText : ''}</span>
      </div>

      {error && !loading && (
        <div className="ent-empty">
          <AlertTriangle size={36} />
          <p>Could not load balance data</p>
          <span>Something went wrong while fetching your balance.</span>
          <button className="ent-btn ent-btn-secondary" onClick={() => fetchData()} style={{ marginTop: '1rem' }}>
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      )}

      {/* Available balance hero + 2x2 mini cards */}
      <div className="bal-hero-grid">
        <div className="bal-hero">
          <div className="bal-hero__top">
            <span className="bal-hero__label">Available balance</span>
            <span className="bal-hero__live">
              <span className="bal-hero__live-dot" aria-hidden="true" />
              Live
            </span>
          </div>
          {loading ? (
            <span className="bal-hero__value"><Skeleton variant="text" width={200} height={42} /></span>
          ) : (
            <span className="bal-hero__value">
              {formatUSD(shownBalance)}
              <span className="bal-hero__cur">USD</span>
            </span>
          )}
          <div className="bal-hero__rows">
            <div className="bal-hero__row">
              <span>Pending</span>
              <span className="bal-hero__row-val mono">
                {loading ? dash : `$${formatUSD(pendingSettlement)}`}
              </span>
            </div>
            <div className="bal-hero__row">
              {/* Nothing is reserved for refunds any more, so the row reports
                  what was overpaid instead — money that IS in this balance and
                  is worth seeing separately from the rest of it. */}
              <span>Overpaid</span>
              <span className="bal-hero__row-val mono">
                {loading ? dash : `$${formatUSD(totalOverpaid)}`}
              </span>
            </div>
            <div className="bal-hero__row">
              <span>Lifetime received</span>
              <span className="bal-hero__row-val mono">
                {lifetimeReceived != null ? `$${formatUSD(lifetimeReceived)}` : dash}
              </span>
            </div>
          </div>
          <div className="bal-hero__actions">
            <button className="bal-hero__btn bal-hero__btn--filled" onClick={() => navigate('/dashboard/payouts')}>
              <Send size={16} />
              Send to wallet
            </button>
            <button className="bal-hero__btn bal-hero__btn--outline" onClick={() => navigate('/dashboard/payouts')}>
              <Repeat size={16} />
              Convert
            </button>
          </div>
        </div>

        {/* Same card as the transactions summary: tinted icon tile, label, one
            figure, one qualifier. One per row, laid out across rather than
            down: three cards in a 2x2 left a hole, and these sit in half the
            page width where a wide row reads better than a narrow well. */}
        <div className="bal-mini">
          <StatCard
            tone="warn"
            icon={Clock}
            label="Pending"
            value={`$${formatUSD(pendingSettlement)}`}
            sub={`${formatCount(pendingCount)} confirming`}
            loading={loading}
          />
          <StatCard
            tone="info"
            icon={Send}
            label="In transit"
            value={`$${formatUSD(pendingPayoutAmount)}`}
            sub="Pending payout"
            loading={loading}
          />
          {/* <StatCard
            tone="brand"
            icon={Lock}
            label="Reserved"
            value={dash}
            sub="No holds"
            loading={loading}
          /> */}
        </div>
      </div>

      {/* PRD §3.3: Pending conversion summary. Renders nothing when zero. */}
      {pendingConversionCount > 0 && (
        <div className="bal-card bal-conversion">
          <div className="bal-conversion__icon">
            <Repeat size={18} />
          </div>
          <div className="bal-conversion__body">
            <span className="bal-conversion__title">Pending conversion</span>
            <span className="bal-conversion__sub">
              {formatCount(pendingConversionCount)}{' '}
              {pendingConversionCount === 1 ? 'conversion' : 'conversions'} in progress
            </span>
          </div>
          <span className="bal-conversion__amount">${formatUSD(pendingConversionUsd)} in conversion</span>
        </div>
      )}

      {/* Overpayments */}
      <div className="bal-card">
        <div className="bal-card__head">
          <h2>Overpayments</h2>
        </div>
        <div className="bal-card__body">
          <p className="bal-helper">
            Payments that came in above the amount due. The excess is settled with the rest of
            the payment and is included in your payout balance.
          </p>
          {loading ? (
            <div className="op-skeletons">
              {Array.from({ length: 3 }, (_, i) => (
                <div className="op-skeleton-row" key={i}>
                  <Skeleton variant="text" width={70} />
                  <Skeleton variant="text" width={120} />
                  <Skeleton variant="text" width={110} />
                  <Skeleton variant="text" width={80} />
                  <Skeleton variant="text" width={64} />
                </div>
              ))}
            </div>
          ) : overpaidPayments.length > 0 ? (
            <div className="op-table-wrap">
              <table className="op-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Source</th>
                    <th>Asset</th>
                    <th className="op-num">Required</th>
                    <th className="op-num">Paid</th>
                    <th className="op-num">Overpaid</th>
                  </tr>
                </thead>
                <tbody>
                  {overpaidPayments.map((payment) => (
                    <OverpaidPayment
                      key={payment.invoiceId || payment.sessionId}
                      payment={payment}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="ent-empty">
              <CheckCircle size={32} />
              <p>No overpaid payments</p>
              <span>All payments match their amounts.</span>
            </div>
          )}
        </div>
        {totalOverpaid > 0 && (
          <div className="bal-card__foot">
            <span>Total overpaid</span>
            <strong>${formatUSD(totalOverpaid)}</strong>
          </div>
        )}
        </div>


      {/* Recent transactions */}
        {/* Same table as Overview's Recent transactions (shared component),
            so the two pages read identically. */}
        <div className="bal-card">
          <div className="bal-card__head bal-rtx-head">
            <span className="bal-rtx-head__icon" aria-hidden="true">
              <Receipt size={18} />
            </span>
            <span className="bal-rtx-head__text">
              <h2>Recent transactions</h2>
              <span className="bal-rtx-head__sub">Latest payments received from your customers</span>
            </span>
            <button
              type="button"
              className="bal-rtx-viewall"
              onClick={() => navigate('/dashboard/transactions')}
            >
              View all <ArrowRight size={14} />
            </button>
          </div>
          <div className="bal-card__body bal-rtx-body">
            {loading ? (
              <div className="bal-list">
                {Array.from({ length: 5 }, (_, i) => (
                  <div className="bal-line" key={i}>
                    <Skeleton variant="text" width={120} height={28} />
                    <div className="bal-line__right">
                      <Skeleton variant="text" width={90} />
                      <Skeleton variant="text" width={60} />
                    </div>
                  </div>
                ))}
              </div>
            ) : recentTransactions.length > 0 ? (
              <div className="bal-rtx-wrap">
                <RecentTransactionsTable transactions={recentTransactions} />
              </div>
            ) : (
              <div className="ent-empty">
                <p>No transactions yet</p>
                <span>Transactions appear here once payments arrive.</span>
              </div>
            )}
          </div>
        </div>

      <style>{`
        /* ── Overpayments table ─────────────────────────────────────── */
        .op-table-wrap { overflow-x: auto; }
        .op-table { width: 100%; border-collapse: collapse; min-width: 720px; }
        .op-table th {
          text-align: left;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--t-500);
          padding: 0 var(--space-3) var(--space-2);
          border-bottom: 1px solid var(--line);
          white-space: nowrap;
        }
        .op-table td {
          padding: 12px var(--space-3);
          border-bottom: 1px solid var(--line);
          vertical-align: middle;
        }
        .op-table tbody tr:last-child td { border-bottom: none; }
        .op-table tbody tr:hover { background: var(--bg-inset); }
        /* Figures are compared down the column, so they align on the decimal. */
        .op-table .op-num { text-align: right; }

        .op-date { display: block; font-size: 13px; font-weight: 600; color: var(--ink); }
        .op-time { display: block; font-size: 11.5px; color: var(--t-500); font-variant-numeric: tabular-nums; }
        .op-customer { font-size: 13px; font-weight: 600; color: var(--ink); }
        /* Uppercased for legibility alongside the INV- references, which already
           are. Done in CSS rather than by transforming the string, so the DOM
           and the title tooltip keep the reference exactly as the merchant
           supplied it — an order id may well be case-sensitive in their system. */
        .op-id {
          display: block;
          margin-top: 2px;
          font-size: 11px;
          color: var(--t-500);
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }
        .op-muted { color: var(--t-500); }

        .op-asset { display: flex; align-items: center; gap: 8px; min-width: 0; }
        .op-asset > :first-child { flex: none; }
        .op-token { display: block; font-size: 12.5px; font-weight: 600; color: var(--ink); line-height: 1.25; }
        .op-chain { display: block; font-size: 11.5px; color: var(--t-500); line-height: 1.25; }

        .op-plain { font-size: 13px; color: var(--t-700); font-variant-numeric: tabular-nums; }
        /* The excess is the point of the row, so it carries the weight. */
        .op-excess {
          font-size: 14px;
          font-weight: 700;
          color: var(--ok-ink, #0a8f64);
          font-variant-numeric: tabular-nums;
        }

        .op-skeletons { padding: var(--space-2) 0; }
        .op-skeleton-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--space-4);
          padding: 12px var(--space-3);
          border-bottom: 1px solid var(--line);
        }
        .op-skeleton-row:last-child { border-bottom: none; }


        .balance-page .spinning { animation: bal-spin 1s linear infinite; }
        @keyframes bal-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        /* Reconciliation banner */
        .bal-recon {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          padding: var(--space-3) var(--space-4);
          border-radius: var(--r-md);
          margin-bottom: var(--space-4);
          font-size: 0.85rem;
        }
        .bal-recon--ok { background: var(--ok-soft); border: 1px solid var(--ok); color: var(--ok-ink); }
        .bal-recon--info { background: var(--surface); border: 1px solid var(--line); color: var(--t-700); }
        .bal-recon svg { flex-shrink: 0; }
        .bal-recon__body { flex: 1; }
        .bal-recon__body strong { color: var(--ink); }
        .bal-recon__meta { color: var(--t-500); white-space: nowrap; }

        /* Pending conversion summary (PRD §3.3) */
        .bal-conversion {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          padding: var(--space-3) var(--space-4);
          margin-bottom: var(--space-4);
        }
        .bal-conversion__icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          flex-shrink: 0;
          border-radius: var(--r-sm);
          background: var(--info-soft);
          color: var(--info-ink);
        }
        .bal-conversion__body { flex: 1; display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .bal-conversion__title { font-size: 0.875rem; font-weight: 600; color: var(--ink); }
        .bal-conversion__sub { font-size: 0.75rem; color: var(--t-500); }
        .bal-conversion__amount {
          font-family: var(--font-mono);
          font-size: 0.95rem;
          font-weight: 700;
          color: var(--ink);
          white-space: nowrap;
        }

        /* Generic card used across the page */
        .bal-card {
          background: var(--surface);
          border: 1px solid var(--line);
          border-radius: var(--r-lg);
          box-shadow: var(--sh-card);
        }
        .bal-card__head {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: var(--space-3);
          padding: var(--space-4);
          border-bottom: 1px solid var(--line);
        }
        .bal-card .bal-card__body{
	         height: 300px;
           overflow-y: scroll;
        }
        .bal-card__head h2 {
          margin: 0;
          font-size: 0.95rem;
          font-weight: 600;
          color: var(--ink);
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }
        .bal-card__body { padding: var(--space-4); }

        /* Recent transactions: header and table match Overview's card. */
        .bal-rtx-head { justify-content: flex-start; gap: 11px; }
        .bal-rtx-head__icon {
          flex: none; display: grid; place-items: center;
          width: 34px; height: 34px; border-radius: 10px;
          background: var(--brand-100, #ece9ff); color: var(--brand-600, #6d5df6);
        }
        .bal-rtx-head__text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
        .bal-card__head.bal-rtx-head h2 { font-size: 15px; font-weight: 700; letter-spacing: -0.01em; }
        .bal-rtx-head__sub { font-size: 12px; color: var(--t-500, #6c7189); }
        .bal-rtx-viewall {
          margin-left: auto; display: inline-flex; align-items: center; gap: var(--space-1);
          background: none; border: none; padding: 0; cursor: pointer; flex-shrink: 0; white-space: nowrap;
          font-family: inherit; font-size: 13px; font-weight: 600; color: var(--accent);
        }
        .bal-rtx-viewall:hover { color: var(--brand-700); }
        .bal-card .bal-card__body.bal-rtx-body { padding: var(--space-2) var(--space-3); }
        .bal-rtx-wrap { overflow-x: auto; }
        .bal-card__foot {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-3) var(--space-4);
          border-top: 1px solid var(--line);
          background: var(--surface-2);
          font-size: 0.85rem;
          color: var(--t-700);
          border-bottom-left-radius: 16px;
          border-bottom-right-radius: 16px;
        }
        .bal-card__foot strong { color: var(--ink); font-size: 1rem; font-family: var(--font-mono); }
        .bal-helper { margin: 0 0 var(--space-3); font-size: 0.75rem; color: var(--t-500); }

        /* Hero + mini cards */
        .bal-hero-grid {
          display: grid;
          grid-template-columns: 1.1fr 1fr;
          gap: 10px;
          margin-bottom: 10px;
          align-items: stretch;
        }

        /* SIGNATURE dark hero balance card */
        .bal-hero {
          /* Layered: the brand gradient underneath, then a violet bloom in the
             top-right from ::before, then a one-pass sheen from ::after. */
          background: var(--grad-balance);
          border-radius: var(--r-xl);
          padding: 16px 18px;
          /* Was written as '// box-shadow: var(--sh-hero);'. '//' is not a CSS
             comment -- the parser treats the line as an invalid declaration and
             discards it up to the semicolon -- so this card has been rendering
             with no shadow at all despite --sh-hero being defined for it. */
          box-shadow: var(--sh-hero);
          border: 1px solid rgba(167, 139, 250, 0.16);
          display: flex;
          flex-direction: column;
          gap: 10px;

          /* Contains the bloom and the sheen, and gives the children a
             stacking context to sit above them. */
          position: relative;
          isolation: isolate;
          overflow: hidden;

          animation: bal-hero-in 620ms
            var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1)) both;
        }

        @keyframes bal-hero-in {
          from { opacity: 0; transform: translate3d(0, 10px, 0); }
          to   { opacity: 1; transform: none; }
        }

        /* Brand bloom. Sits behind the content (z-index: -1 within the card's
           own isolated stacking context, so it cannot escape upward). */
        .bal-hero::before {
          content: '';
          position: absolute;
          top: -45%;
          right: -18%;
          width: 70%;
          height: 170%;
          z-index: -1;
          pointer-events: none;
          background: radial-gradient(
            circle at 50% 50%,
            rgba(139, 92, 246, 0.42) 0%,
            rgba(124, 58, 237, 0.18) 42%,
            transparent 70%
          );
          animation: bal-bloom 9s ease-in-out infinite alternate;
        }

        /* Slow, small drift. Deliberately understated: this sits under live
           financial figures, so it must not pull the eye off them. */
        @keyframes bal-bloom {
          from { transform: translate3d(0, 0, 0) scale(1); opacity: 0.85; }
          to   { transform: translate3d(-6%, 4%, 0) scale(1.08); opacity: 1; }
        }

        /* Single sheen pass on mount, giving the card a glass read. */
        .bal-hero::after {
          content: '';
          position: absolute;
          inset: 0;
          z-index: -1;
          pointer-events: none;
          background: linear-gradient(
            105deg,
            transparent 38%,
            rgba(255, 255, 255, 0.07) 50%,
            transparent 62%
          );
          animation: bal-sheen 1500ms
            var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1)) 320ms both;
        }

        @keyframes bal-sheen {
          from { transform: translate3d(-120%, 0, 0); }
          to   { transform: translate3d(120%, 0, 0); }
        }
        .bal-hero__top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--space-3);
        }
        .bal-hero__label {
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--t-on-dark-dim);
        }
        .bal-hero__live {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 3px 10px;
          border-radius: var(--r-pill);
          background: rgba(15, 185, 129, 0.16);
          color: var(--ok);
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }
        /* The dot itself stays solid and a ring expands out of it. A dot that
           merely blinks reads as something failing to load; a ring travelling
           outward reads as a signal being emitted, which is what "live" means
           here — the figure above is being kept current. */
        .bal-hero__live-dot {
          position: relative;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--ok);
          animation: bal-pulse 1.8s ease-in-out infinite;
        }
        .bal-hero__live-dot::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 1px solid var(--ok);
          animation: bal-ping 1.8s cubic-bezier(0.22, 0.61, 0.36, 1) infinite;
        }
        @keyframes bal-ping {
          0%        { transform: scale(1); opacity: 0.7; }
          70%, 100% { transform: scale(2.8); opacity: 0; }
        }
        @keyframes bal-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.75; transform: scale(0.88); }
        }
        /* Vestibular safety. The Live dot's pulse was already handled here;
           the entrance and ambient animations added above need the same
           treatment, and the bloom in particular must stop rather than drift
           indefinitely. Every animation ends on its natural state and uses
           'both', so disabling them leaves the card fully visible. */
        @media (prefers-reduced-motion: reduce) {
          .bal-hero__live-dot,
          .bal-hero__live-dot::after {
            animation: none;
          }
          /* The ring would otherwise sit frozen at its start size, drawing a
             second circle around the dot for no reason. */
          .bal-hero__live-dot::after { display: none; }
          .bal-hero__btn svg { transition: none; }
          .bal-hero__btn--filled:hover svg,
          .bal-hero__btn--outline:hover svg { transform: none; }

          .bal-hero,
          .bal-hero::before,
          .bal-hero::after,
          .bal-hero__value,
          .bal-hero__row {
            animation: none;
          }

          .bal-hero__btn:hover {
            transform: none;
          }
        }
        .bal-hero__value {
          animation: bal-value-in 520ms
            var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1)) 120ms both;
          /* Mono is kept deliberately: the digits change every frame while the
             count-up runs, and a proportional face would make the whole line
             jitter as widths shift. */
          font-family: var(--font-mono);
          font-size: 32px;
          font-weight: 700;
          color: #fff;
          line-height: 1.05;
          letter-spacing: -0.02em;
          display: flex;
          align-items: baseline;
          gap: 0.4rem;
        }
        @keyframes bal-value-in {
          from { opacity: 0; transform: translate3d(0, 8px, 0); }
          to   { opacity: 1; transform: none; }
        }

        .bal-hero__cur {
          font-family: var(--font-sans);
          font-size: 0.95rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          color: var(--t-on-dark-dim);
        }
        .bal-hero__chains {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-2);
        }
        .bal-hero__chain {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 4px 10px;
          border-radius: var(--r-pill);
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: var(--t-on-dark);
          font-size: 0.72rem;
          font-weight: 500;
        }
        .bal-hero__chain-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .bal-hero__rows {
          display: flex;
          flex-direction: column;
          gap: 5px;
          padding-top: 10px;
          border-top: 1px solid rgba(255, 255, 255, 0.09);
        }
        .bal-hero__row {
          animation: bal-row-in 420ms
            var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1)) both;
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 0.78rem;
          color: var(--t-on-dark-dim);
        }
        .bal-hero__row-val { font-family: var(--font-mono); color: var(--t-on-dark); font-weight: 600; }
        .bal-hero__row:nth-child(1) { animation-delay: 240ms; }
        .bal-hero__row:nth-child(2) { animation-delay: 300ms; }
        .bal-hero__row:nth-child(3) { animation-delay: 350ms; }

        @keyframes bal-row-in {
          from { opacity: 0; transform: translate3d(0, 6px, 0); }
          to   { opacity: 1; transform: none; }
        }

        .bal-hero__actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
          margin-top: auto;
          padding-top: 4px;
        }
        .bal-hero__btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.45rem;
          padding: 0.5rem 0.9rem;
          border-radius: var(--r-md);
          font-family: var(--font-sans);
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition:
            opacity 160ms var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1)),
            background 160ms var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1)),
            border-color 160ms var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1)),
            transform 160ms var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1)),
            box-shadow 160ms var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1));
        }
        .bal-hero__btn--filled {
          background: #fff;
          color: var(--ink-900);
          border: 1px solid #fff;
        }
        /* Each glyph moves the way its action does, so the motion carries the
           meaning rather than just decorating the hover. */
        .bal-hero__btn svg {
          transition: transform 280ms var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1));
        }
        .bal-hero__btn--filled:hover {
          opacity: 0.94;
          transform: translateY(-1px);
          box-shadow: 0 8px 22px rgba(139, 92, 246, 0.34);
        }
        /* The send glyph is a paper plane: it leaves, up and to the right. */
        .bal-hero__btn--filled:hover svg { transform: translate(3px, -3px); }
        /* The convert glyph is a two-way arrow: half a turn is the swap. */
        .bal-hero__btn--outline:hover svg { transform: rotate(180deg); }
        .bal-hero__btn--outline {
          background: rgba(255, 255, 255, 0.07);
          color: #fff;
          border: 1px solid rgba(255, 255, 255, 0.16);
        }
        .bal-hero__btn--outline:hover {
          background: rgba(255, 255, 255, 0.13);
          border-color: rgba(167, 139, 250, 0.45);
          transform: translateY(-1px);
        }

        /* Three rows of one. Two columns would leave a hole now that there
           are three cards, and three columns in half the page width gives
           each one a narrow well. The rows stack to roughly the hero's own
           height, which is what keeps the pair level. */
        .bal-mini {
          display: grid;
          grid-template-rows: repeat(3, minmax(0, 1fr));
          gap: 10px;
          margin: 0;
        }
        /* Laid out across: tile, then label over qualifier, then the figure
           hard right. The card's own stack would leave most of a wide row
           empty. */
        .bal-mini .stat-card {
          display: grid;
          grid-template-columns: auto minmax(0, 1fr);
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
        }
        .bal-mini .stat-card__icon { width: 34px; height: 34px; border-radius: 10px; }
        .bal-mini .stat-card__icon svg { width: 17px; height: 17px; }
        .bal-mini .stat-card__body {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: center;
          column-gap: 14px;
          row-gap: 1px;
        }
        .bal-mini .stat-card__label { grid-area: 1 / 1; font-size: 13px; }
        .bal-mini .stat-card__sub { grid-area: 2 / 1; font-size: 11.5px; }
        .bal-mini .stat-card__valrow { grid-area: 1 / 2 / span 2 / 2; justify-self: end; }
        .bal-mini .stat-card__value { font-size: 20px; }
        @media (max-width: 560px) {
          .bal-mini { grid-template-rows: none; }
        }

        /* Section heads */
        .bal-section-head {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: var(--space-5) 0 var(--space-3);
        }
        .bal-section-head h2 {
          margin: 0;
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--t-500);
        }

        /* Lists (refunds due / recent / refunds) */
        .bal-list { display: flex; flex-direction: column; }
        .bal-line {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          padding: var(--space-3) 0;
          border-bottom: 1px solid var(--line);
        }
        .bal-line:last-child { border-bottom: none; }
        .bal-line--click { cursor: pointer; }
        .bal-line__main { flex: 1; display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .bal-line__title { font-family: var(--font-mono); font-size: 0.875rem; font-weight: 600; color: var(--ink); }
        .bal-line__sub { font-size: 0.72rem; color: var(--t-500); display: flex; align-items: center; gap: 0.4rem; }
        .bal-line__sub.mono { font-family: var(--font-mono); }
        .bal-line__right { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; margin-left: auto; }
        .bal-line__excess { font-family: var(--font-mono); font-size: 0.9rem; font-weight: 700; color: var(--warn-ink); min-width: 72px; text-align: right; }

        .bal-type {
          display: inline-block;
          padding: 1px 5px;
          border-radius: var(--r-sm);
          font-size: 0.6rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .bal-type.invoice { background: var(--info-soft); color: var(--info-ink); }
        .bal-type.session { background: var(--bg-inset); color: var(--t-700); }

        /* Cancel pending refund */
        .bal-refund-cancel {
          flex-shrink: 0;
          padding: 0.4rem 0.75rem;
          background: var(--surface);
          border: 1px solid var(--line-strong);
          border-radius: var(--r-sm);
          font-size: 0.78rem;
          font-weight: 600;
          color: var(--ink);
          cursor: pointer;
        }
        .bal-refund-cancel:hover:not(:disabled) { background: var(--bg-inset); }
        .bal-refund-cancel:disabled { opacity: 0.6; cursor: not-allowed; }

        /* Empty state inside cards */
        .ent-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.35rem;
          padding: var(--space-6) var(--space-4);
          text-align: center;
          color: var(--t-700);
        }
        .ent-empty svg { color: var(--t-400); }
        .ent-empty p { margin: 0; font-weight: 600; color: var(--ink); }
        .ent-empty span { font-size: 0.8rem; color: var(--t-500); }

        /* Refund modal (re-skinned with ent tokens) */
        .refund-modal-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.5);
          display: flex; align-items: center; justify-content: center;
          z-index: 1000; padding: 1rem;
        }
        .refund-modal {
          background: var(--surface); border: 1px solid var(--line);
          border-radius: var(--r-lg); width: 100%; max-width: 440px; overflow: hidden;
        }
        .refund-modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: var(--space-4) var(--space-5); border-bottom: 1px solid var(--line);
        }
        .refund-modal-header h2 { margin: 0; font-size: 1rem; font-weight: 600; color: var(--ink); }
        .refund-modal-close {
          display: flex; align-items: center; justify-content: center;
          background: transparent; border: none; color: var(--t-500);
          cursor: pointer; padding: 0.25rem; border-radius: var(--r-sm);
        }
        .refund-modal-close:hover:not(:disabled) { background: var(--bg-inset); color: var(--ink); }
        /* Refund detail popup */
        .refund-detail { padding: var(--space-5); display: flex; flex-direction: column; gap: var(--space-4); }
        .refund-detail__amount {
          display: flex; align-items: flex-start; justify-content: space-between; gap: var(--space-3);
        }
        .refund-detail__label {
          display: block; font-size: 0.7rem; font-weight: 600; text-transform: uppercase;
          letter-spacing: 0.05em; color: var(--t-500); margin-bottom: 0.2rem;
        }
        .refund-detail__value {
          font-family: var(--font-mono); font-size: 1.6rem; font-weight: 700; color: var(--ink);
        }
        .refund-detail__cur { font-size: 0.8rem; font-weight: 600; color: var(--t-500); }
        .refund-detail__reason {
          display: flex; gap: var(--space-3); padding: var(--space-3) var(--space-4);
          background: var(--err-soft); border: 1px solid var(--err); border-radius: var(--r-md);
          color: var(--err-ink);
        }
        .refund-detail__reason svg { flex-shrink: 0; margin-top: 0.15rem; }
        .refund-detail__reason strong { display: block; font-size: 0.78rem; margin-bottom: 0.15rem; }
        .refund-detail__reason p {
          margin: 0; font-size: 0.85rem; line-height: 1.45; overflow-wrap: anywhere;
        }
        .refund-detail__rows {
          display: flex; flex-direction: column; gap: var(--space-3);
          padding-top: var(--space-3); border-top: 1px solid var(--line);
        }
        .refund-detail__row {
          display: flex; align-items: center; justify-content: space-between; gap: var(--space-4);
        }
        .refund-detail__row > span:first-child { font-size: 0.8rem; color: var(--t-500); }
        .refund-detail__chip {
          display: inline-flex; align-items: center; gap: 0.5rem;
          font-size: 0.875rem; font-weight: 600; color: var(--ink);
        }
        .refund-detail__mono {
          font-family: var(--font-mono); font-size: 0.8rem; font-weight: 500; color: var(--t-700);
        }
        .refund-detail__link {
          display: inline-flex; align-items: center; gap: 0.35rem;
          font-size: 0.8rem; font-weight: 500; color: var(--brand-500); text-decoration: none;
        }
        .refund-detail__link:hover { text-decoration: underline; }
        .refund-detail__link svg { flex-shrink: 0; }
        .refund-detail__note {
          font-size: 0.85rem; color: var(--t-700); text-align: right; max-width: 60%;
          overflow-wrap: anywhere;
        }

        .refund-form { padding: var(--space-5); display: flex; flex-direction: column; gap: var(--space-4); }
        .refund-field { display: flex; flex-direction: column; gap: 0.4rem; }
        .refund-field span { font-size: 0.8rem; font-weight: 500; color: var(--t-700); }
        .refund-field input {
          padding: 0.6rem 0.75rem; background: var(--surface-2);
          border: 1px solid var(--line-strong); border-radius: var(--r-sm);
          font-size: 0.875rem; color: var(--ink);
          font-family: var(--iv-sans);
        }
        .refund-field input:focus { outline: none; border-color: var(--brand-500); }
        .refund-form-actions { display: flex; justify-content: flex-end; gap: var(--space-3); margin-top: 0.25rem; }
        .refund-cancel-btn {
          padding: 0.55rem 1rem; background: var(--surface);
          border: 1px solid var(--line-strong); border-radius: var(--r-sm);
          font-size: 0.875rem; font-weight: 500; color: var(--ink); cursor: pointer;
        }
        .refund-cancel-btn:hover:not(:disabled) { background: var(--bg-inset); }
        .refund-submit-btn {
          padding: 0.55rem 1rem; background: var(--primary);
          border: 1px solid var(--primary); border-radius: var(--r-sm);
          font-size: 0.875rem; font-weight: 600; color: #fff; cursor: pointer;
          font-family: var(--iv-sans);
        }
        .refund-submit-btn:disabled, .refund-cancel-btn:disabled, .refund-modal-close:disabled {
          opacity: 0.6; cursor: not-allowed;
        }

        /* Responsive */
        @media (max-width: 991px) {
          .bal-hero-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 576px) {
          .bal-hero__value { font-size: 27px; }
          .bal-hero__btn { min-height: 44px; }
          .bal-line { flex-wrap: wrap; }
          .bal-refund-cancel { min-height: 44px; padding: 0.5rem 1rem; }
          .refund-modal { max-width: 100%; border-radius: var(--r-lg) var(--r-lg) 0 0; align-self: flex-end; }
          .refund-modal-overlay { align-items: flex-end; padding: 0; }
          .bal-line__main{min-width: auto;}
        }
      `}</style>
    </div>
  );
};

export default Balance;
